import { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore"
import { db } from "../../firebase"
import { Box, Button, Typography } from "@mui/material"
import PrintIcon from '@mui/icons-material/Print'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import dayjs from "dayjs"
import { IBusinessHoliday, isDateInHolidays } from "../../utils/businessHolidays"
import { HEALTH_CHECK_ITEMS } from "../../utils/healthCheckItems"
import { IEmployee } from "./Employees"

const FACILITY_NAME = 'りんどう'

interface HealthCheckData {
  day: string
  checks?: Record<string, string>
  status?: string
}

const MONTHS = [
  "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"
]

const HealthCheckDocument = () => {
  const navigate = useNavigate()
  const { employeeId } = useParams<{ employeeId: string }>()
  const [searchParams] = useSearchParams()
  const year = searchParams.get('year') || dayjs().format('YYYY')
  const month = MONTHS.includes(searchParams.get('month') || '') ? (searchParams.get('month') as string) : dayjs().format('MM')

  const [employee, setEmployee] = useState<IEmployee | null>(null)
  const [notFound, setNotFound] = useState<boolean>(false)
  const [records, setRecords] = useState<Record<string, HealthCheckData>>({})
  const [holidays, setHolidays] = useState<IBusinessHoliday[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth()

  useEffect(() => {
    if (!employeeId) return

    const load = async () => {
      setLoading(true)
      try {
        const employeeSnap = await getDoc(doc(db, "employees", employeeId))
        if (!employeeSnap.exists()) {
          setNotFound(true)
          return
        }
        setEmployee({ id: employeeSnap.id, ...employeeSnap.data() } as IEmployee)

        const [checksSnap, holidaysSnap] = await Promise.all([
          getDocs(query(
            collection(db, "healthChecks"),
            where("employeeId", "==", employeeId),
            where("year", "==", year),
            where("month", "==", month)
          )),
          getDocs(collection(db, "businessHolidays")),
        ])

        const recordsMap: Record<string, HealthCheckData> = {}
        checksSnap.docs.forEach(d => {
          const data = d.data() as HealthCheckData
          recordsMap[data.day] = data
        })
        setRecords(recordsMap)
        setHolidays(holidaysSnap.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday)))
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [employeeId, year, month])

  useEffect(() => {
    if (!employee) return
    const originalTitle = document.title
    document.title = `従業員健康チェック表_${employee.name}_${year}${month}.pdf`
    return () => { document.title = originalTitle }
  }, [employee, year, month])

  const isHoliday = (day: number) => {
    const dateStr = `${year}-${month}-${day.toString().padStart(2, '0')}`
    return isDateInHolidays(dateStr, holidays)
  }

  if (notFound) {
    return (
      <Box m={4}>
        <Typography variant="h5">該当する従業員が見つかりません</Typography>
        <Box mt={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/health-check')}>
            従業員健康チェック表に戻る
          </Button>
        </Box>
      </Box>
    )
  }

  if (loading || !employee) {
    return <Box m={4}><Typography>読み込み中...</Typography></Box>
  }

  return (
    <Box>
      {/* 印刷時にブラウザ既定のヘッダ/フッタ（URL・日付・ページ番号）を非表示にする */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @page { size: A4 landscape; margin: 10mm; }
        @media print {
          html, body { margin: 0 !important; }
        }
      `}} />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          '@media print': { display: 'none' },
        }}
      >
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/health-check')}>
          戻る
        </Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          印刷 / PDF保存
        </Button>
      </Box>

      <Box
        sx={{
          maxWidth: '297mm',
          mx: 'auto',
          p: '10mm',
          bgcolor: 'white',
          color: '#000',
          fontFamily: '"Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif',
          '@media print': {
            maxWidth: 'none',
            margin: 0,
            padding: 0,
            boxShadow: 'none',
          },
        }}
      >
        <Typography variant="h5" align="center" fontWeight="bold" mb={1}>
          従業員健康チェック表
        </Typography>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography>施設名：{FACILITY_NAME}</Typography>
          <Typography>従業員名：{employee.name}</Typography>
          <Typography>{year}年 {parseInt(month, 10)}月</Typography>
        </Box>

        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <Box component="thead">
            <Box component="tr">
              {['日付', '従業員名', ...HEALTH_CHECK_ITEMS.map(i => i.label)].map((label) => (
                <Box
                  component="th"
                  key={label}
                  sx={{ border: '1px solid #000', p: '4px', backgroundColor: '#f0f0f0' }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const holiday = isHoliday(day)
              const record = records[day.toString()]

              return (
                <Box component="tr" key={day}>
                  <Box component="td" sx={{ border: '1px solid #000', p: '4px', textAlign: 'center' }}>{day}</Box>
                  <Box component="td" sx={{ border: '1px solid #000', p: '4px', textAlign: 'center' }}>
                    {holiday ? '' : employee.name}
                  </Box>
                  {holiday ? (
                    <Box
                      component="td"
                      colSpan={HEALTH_CHECK_ITEMS.length}
                      sx={{ border: '1px solid #000', p: '4px', textAlign: 'center' }}
                    >
                      休業日
                    </Box>
                  ) : (
                    HEALTH_CHECK_ITEMS.map(item => (
                      <Box
                        component="td"
                        key={item.key}
                        sx={{ border: '1px solid #000', p: '4px', textAlign: 'center' }}
                      >
                        {record?.status === 'completed' ? (record.checks?.[item.key] || '') : ''}
                      </Box>
                    ))
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>

        <Typography sx={{ mt: 1, fontSize: '11px' }}>
          ※ 原則として作業開始前に確認。異常がある場合は責任者へ報告し、食品取扱作業への従事可否を判断する。
        </Typography>
      </Box>
    </Box>
  )
}

export default HealthCheckDocument
